// src/hooks/useReduxSearch.ts
// Redux-based search hook to replace SearchContext

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  setSearchTerm,
  setSearchResults,
  addToSearchHistory,
  clearSearchHistory,
  setIsSearching,
  setFilters,
  clearSearch,
  setError,
  type SearchFilters,
  type SearchResult,
} from "../store/slices/searchSlice";
import { errorMessage } from "@jkast90/kast-common/format";
import { logger } from "@common/utils/logger";

export function useReduxSearch() {
  const dispatch = useAppDispatch();
  const searchState = useAppSelector((state) => state.search);

  const updateSearchTerm = useCallback(
    (term: string) => {
      dispatch(setSearchTerm(term));
    },
    [dispatch],
  );

  const performSearch = useCallback(
    async (
      term: string,
      searchFunction: (term: string) => Promise<SearchResult[]>,
    ) => {
      if (!term?.trim()) {
        dispatch(clearSearch());
        return;
      }

      dispatch(setIsSearching(true));
      dispatch(setError(null));

      try {
        const results = await searchFunction(term);
        dispatch(setSearchResults(results));
        dispatch(addToSearchHistory(term));
      } catch (error) {
        logger.error("Search error:", error);
        dispatch(setError(errorMessage(error)));
      } finally {
        dispatch(setIsSearching(false));
      }
    },
    [dispatch],
  );

  const updateFilters = useCallback(
    (newFilters: Partial<SearchFilters>) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  const clear = useCallback(() => {
    dispatch(clearSearch());
  }, [dispatch]);

  const clearHistory = useCallback(() => {
    dispatch(clearSearchHistory());
  }, [dispatch]);

  return {
    // State
    searchTerm: searchState.searchTerm,
    searchResults: searchState.searchResults,
    searchHistory: searchState.searchHistory,
    isSearching: searchState.isSearching,
    filters: searchState.filters,
    error: searchState.error,

    // Actions
    setSearchTerm: updateSearchTerm,
    setSearchResults: (results: SearchResult[]) =>
      dispatch(setSearchResults(results)),
    performSearch,
    updateFilters,
    clearSearch: clear,
    clearSearchHistory: clearHistory,
    setIsSearching: (searching: boolean) => dispatch(setIsSearching(searching)),
  };
}

// Convenience hook with same API as context
export const useSearch = useReduxSearch;
